import { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../api/axios';
import styles from '../../App.module.css';
import { NotificationManager } from 'react-notifications';
import Agendamentos, { openModal as openAgendamento } from '../agendamentos';

const locales = {
  'pt-BR': ptBR,


};

// sa

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

const Consulados = {
  54: 'Brasília',
  55: 'Rio de Janeiro',
  56: 'São Paulo',
  57: 'Recife',
  128: 'Porto Alegre'
};

const colors = [
  'red', 'blue', 'green', 'orange', 'purple', 'black', 'pink', 'brown', 'grey', 'yellow'
];

const colorMap = {};

const getColorByConsulado = (consulado) => {
  if (!colorMap[consulado]) {
    const assignedColorsCount = Object.keys(colorMap).length;
    colorMap[consulado] = colors[assignedColorsCount % colors.length];
  }
  return colorMap[consulado];
};

const CalendarioEntrevista = () => {
  const [visibleConsulados, setVisibleConsulados] = useState(new Set());
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoadingVendedores, setIsLoadingVendedores] = useState(true);

  const parseAppointments = useCallback((appointments) => {
    return appointments
      .map((appointment, index) => {
        try {
          const entrevista = JSON.parse(appointment.entrevista);
          const start = new Date(entrevista.data_horario);
          const end = new Date(entrevista.data_horario);

          const consuladoId = entrevista.consulado;
          const consuladoNome = Consulados[consuladoId] || 'Consulado não informado';

          return {
            id: index,
            title: `${appointment.email} - ${consuladoNome}`,
            start,
            end,
            allDay: false,
            consulado: consuladoNome,
            color: getColorByConsulado(consuladoNome),
          };
        } catch (error) {
          console.error('Error parsing entrevista:', error, appointment);
          return null;
        }
      })
      .filter((event) => event !== null);
  }, []);

  const initializeLegend = useCallback((parsedEvents) => {
    const visibleConsuladosSet = new Set();
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    parsedEvents.forEach((event) => {
      const start = new Date(event.start);
      const end = new Date(event.end);
      if (start <= endOfMonth && end >= startOfMonth) {
        visibleConsuladosSet.add(event.consulado);
      }
    });

    setVisibleConsulados(visibleConsuladosSet);
  }, []);

  const getVendedoresProdutores = useCallback(async () => {
    try {
      const resVendedoresProdutores = await api.get('/vendedores-produtores');
      if (
        resVendedoresProdutores.data &&
        Array.isArray(resVendedoresProdutores.data.data)
      ) {
        // resposta validada; sem uso adicional nesta tela
      } else {
        console.error('Formato inesperado da resposta:', resVendedoresProdutores.data);
        NotificationManager.error('Erro ao carregar vendedores e produtores.', 'Erro');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      NotificationManager.error('Erro ao carregar dados.', 'Erro');
    } finally {
      setIsLoadingVendedores(false);
    }
  }, []);

  const fetchAgendamentos = useCallback(async () => {
    try {
      const response = await api.get('/agendamentos');
      const parsedEvents = parseAppointments(response.data);
      setEvents(parsedEvents);
      initializeLegend(parsedEvents);
    } catch (error) {
      console.error('Erro ao listar agendamentos:', error);
    }
  }, [initializeLegend, parseAppointments]);

  useEffect(() => {
    getVendedoresProdutores();
  }, [getVendedoresProdutores]);

  useEffect(() => {
    if (!isLoadingVendedores) {
      fetchAgendamentos();
    }
  }, [isLoadingVendedores, fetchAgendamentos]);

  const eventStyleGetter = (event) => {
    const backgroundColor = event.color;
    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.8rem',
      }
    };
  };

  const EventComponent = ({ event }) => {
    return <span style={{ fontSize: '0.8rem' }}>{event.title}</span>;
  };

  const handleRangeChange = (range) => {
    const visibleConsuladosSet = new Set();
    const rangeStart = new Date(range.start);
    const rangeEnd = new Date(range.end);

    events.forEach((event) => {
      const start = new Date(event.start);
      const end = new Date(event.end);
      if (start <= rangeEnd && end >= rangeStart) {
        visibleConsuladosSet.add(event.consulado);
      }
    });

    setVisibleConsulados(visibleConsuladosSet);
  };

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      .rbc-today {
        background-color: #c4c4ce !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const CustomToolbar = (toolbar) => {
    const goToBack = () => toolbar.onNavigate('PREV');
    const goToToday = () => toolbar.onNavigate('TODAY');
    const goToNext = () => toolbar.onNavigate('NEXT');

    return (
      <div
        className="rbc-toolbar"
        style={{
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div>
          <button
            className={styles.cadastroCliente}
            onClick={() => openAgendamento()}
            style={{
              background: 'var(--corPrimaria)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 14px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Acrescentar
          </button>
        </div>

        <div>
          <span className="rbc-btn-group">
            <button
              style={{ marginRight: 10, border: '2px solid #A5A5A5', borderRadius: 20 }}
              onClick={goToBack}
            >
              Anterior
            </button>
            <button
              style={{ marginRight: 10, border: '2px solid #A5A5A5', borderRadius: 20 }}
              onClick={goToToday}
            >
              Hoje
            </button>
            <button
              style={{ marginRight: 10, border: '2px solid #A5A5A5', borderRadius: 20 }}
              onClick={goToNext}
            >
              Próximo
            </button>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: '100%', maxWidth: '600px' }}>
      <div className={styles.calendarioCSS}>
        {isLoadingVendedores ? (
          <p>Carregando dados...</p>
        ) : (
          <>
            <h2 style={{ textAlign: 'center', marginBottom: '10px' }}>
              Entrevistas{" "}
              {format(currentDate, 'MMMM yyyy', { locale: ptBR }).replace(
                /^./,
                (char) => char.toUpperCase()
              )}
            </h2>

            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600, width: '90%', maxWidth: '600px', fontSize: '1rem' }}
              culture="pt-BR"
              defaultView="month"
              views={['month']}
              eventPropGetter={eventStyleGetter}
              components={{ toolbar: CustomToolbar, event: EventComponent }}
              onRangeChange={handleRangeChange}
              messages={{
                today: 'Hoje',
                previous: 'Anterior',
                next: 'Próximo'
              }}
              onNavigate={(date) => setCurrentDate(date)}
            />

            <div className={styles.legendContainer}>
              {Array.from(visibleConsulados).map((consulado) => (
                <div
                  key={consulado}
                  className={styles.legendItem}
                  style={{
                    border: `3px solid ${getColorByConsulado(consulado)}`,
                    borderRadius: '20px',
                    backgroundColor: "#e0e0e0",
                    padding: "5px"
                  }}
                >
                  {consulado}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div style={{ zIndex: 1000, position: 'fixed', bottom: 0, width: '100%' }}>
        <Agendamentos />
      </div>
    </div>
  );
};

export default CalendarioEntrevista;